import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from 'react-helmet-async';
import AdminLayout from './layouts/AdminLayout';
import { 
  Download, 
  ArrowLeft, 
  User, 
  Mail, 
  Phone, 
  MapPin, 
  Building, 
  Award, 
  Briefcase,
  Calendar,
  FileText,
  GraduationCap,
  Users,
  Shield,
  Printer,
  Save,
  CheckCircle,
  Clock,
  Eye,
  XCircle,
  Edit,
  Star
} from "lucide-react";
import axios from "axios";

export const API_URL =
  process.env.REACT_APP_API_URL || "https://sbg-backend.onrender.com";

const StatusBadge = ({ status }) => {
  const statusConfig = {
    'Pending': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: Clock },
    'Reviewed': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: Eye },
    'Interviewed': { color: 'bg-purple-100 text-purple-800 border-purple-200', icon: Users },
    'Offered': { color: 'bg-green-100 text-green-800 border-green-200', icon: CheckCircle },
    'Rejected': { color: 'bg-red-100 text-red-800 border-red-200', icon: XCircle },
  };

  const config = statusConfig[status] || statusConfig['Pending'];
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium border ${config.color}`}>
      <Icon className="w-4 h-4 mr-1.5" />
      {status}
    </span>
  );
};

const InfoCard = ({ title, icon: Icon, children, className = "", noPrint = false }) => (
  <div className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${className} ${noPrint ? 'print:hidden' : ''}`}>
    <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
      <h3 className="text-lg font-semibold text-gray-900 flex items-center">
        <Icon className="w-5 h-5 mr-2 text-gray-600" />
        {title}
      </h3>
    </div>
    <div className="p-6">
      {children}
    </div>
  </div>
);

const DataField = ({ label, value, icon: Icon }) => {
  if (!value) return null;
  
  return (
    <div className="flex items-start space-x-3">
      {Icon && <Icon className="w-4 h-4 mt-1 text-gray-500 flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-500">{label}</p>
        <p className="text-sm text-gray-900 break-words">{value}</p>
      </div>
    </div>
  );
};

const ApplicationDetails = () => {
  const [application, setApplication] = useState(null);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const { id } = useParams();

  useEffect(() => {
    const fetchApplicationAndJob = async () => {
      try {
        console.log("Fetching application with ID:", id);
        const applicationResponse = await axios.get(
          `${API_URL}/applications/${id}`
        );
        const applicationData = applicationResponse.data;
        console.log("Received application data:", applicationData);
        setApplication(applicationData);

        if (applicationData.jobId) {
          console.log("Fetching job with ID:", applicationData.jobId);
          const jobResponse = await axios.get(
            `${API_URL}/jobs/${applicationData.jobId}`
          );
          const jobData = jobResponse.data;
          console.log("Received job data:", jobData);
          setJob(jobData);
        }
      } catch (error) {
        console.error("Error fetching application or job:", error);
        setError("Failed to load application details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchApplicationAndJob();
  }, [id]);

  const handleStatusChange = async (newStatus) => {
    setStatusLoading(true);
    try {
      await axios.put(`${API_URL}/applications/${id}`, {
        status: newStatus,
      });
      setApplication({ ...application, status: newStatus });
    } catch (error) {
      console.error("Error updating application status:", error);
      alert("Failed to update application status. Please try again.");
    } finally {
      setStatusLoading(false);
    }
  };

  const handleViewResume = async () => {
    if (application && application.resumePath) {
      try {
        const response = await axios.get(
          `${API_URL}/api/getSecureDownloadUrl`,
          {
            params: {
              filePath: application.resumePath
            }
          }
        );

        if (response.data && response.data.url) {
          window.open(response.data.url, "_blank");
        } else {
          throw new Error('No URL received');
        }
      } catch (error) {
        console.error("Error getting secure download URL:", error);
        alert("Failed to access resume. Please try again.");
      }
    } else {
      alert("Resume not available");
    }
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-gray-600">Loading application details...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 text-lg">{error}</p>
            <Link
              to="/admin/applications"
              className="mt-4 inline-flex items-center text-primary hover:text-primary-dark"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Applications
            </Link>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!application) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 text-lg">Application not found.</p>
            <Link
              to="/admin/applications"
              className="mt-4 inline-flex items-center text-primary hover:text-primary-dark"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Applications
            </Link>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <Helmet>
        <title>Application Details - {application.firstName} {application.lastName} | SBG Admin</title>
      </Helmet>

      {/* Print Styles */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .print-container, .print-container * {
              visibility: visible;
            }
            .print-container {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
            .print:hidden {
              display: none !important;
            }
            .bg-gray-50 {
              background-color: #f9fafb !important;
            }
            .shadow-sm {
              box-shadow: none !important;
            }
            .border {
              border: 1px solid #e5e7eb !important;
            }
          }
        `}
      </style>

      <div className="print-container">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8 print:hidden">
          <div>
            <Link
              to="/admin/applications"
              className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-2 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Applications
            </Link>
            <h1 className="text-3xl font-bold text-gray-900">Application Details</h1>
            <p className="text-gray-600 mt-1">
              Review and manage application from {application.firstName} {application.lastName}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handlePrint}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Application
            </button>
          </div>
        </div>

        {/* Applicant Summary Card */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 mb-8">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                  <User className="w-8 h-8 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-2xl font-bold text-gray-900">
                    {application.firstName} {application.lastName}
                  </h2>
                  <div className="flex flex-wrap items-center gap-4 mt-2 text-gray-600">
                    <div className="flex items-center">
                      <Mail className="w-4 h-4 mr-2" />
                      <span className="break-all">{application.email}</span>
                    </div>
                    {application.phone && (
                      <div className="flex items-center">
                        <Phone className="w-4 h-4 mr-2" />
                        <span>{application.phone}</span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-2" />
                      <span>Applied {new Date(application.appliedDate).toLocaleDateString()}</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col lg:items-end gap-4">
                <StatusBadge status={application.status || 'Pending'} />
                <div className="print:hidden">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Update Status
                  </label>
                  <select
                    value={application.status || "Pending"}
                    onChange={(e) => handleStatusChange(e.target.value)}
                    disabled={statusLoading}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
                  >
                    <option value="Pending">Pending</option>
                    <option value="Reviewed">Reviewed</option>
                    <option value="Interviewed">Interviewed</option>
                    <option value="Offered">Offered</option>
                    <option value="Rejected">Rejected</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="xl:col-span-2 space-y-8">
            {/* Application Type */}
            <InfoCard title="Application Type" icon={Briefcase}>
              {application.isGeneralApplication ? (
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-500 mr-2" />
                  <span className="text-lg font-medium text-blue-600">General Application</span>
                </div>
              ) : job ? (
                <div className="space-y-3">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Position</p>
                    <p className="text-lg font-semibold text-gray-900">{job.title}</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <DataField label="Company" value={job.company} icon={Building} />
                    <DataField label="Location" value={job.location} icon={MapPin} />
                  </div>
                </div>
              ) : (
                <p className="text-gray-600">Specific Job Application</p>
              )}
            </InfoCard>

            {/* Contact Information */}
            <InfoCard title="Contact Information" icon={User}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <DataField label="Full Name" value={`${application.firstName} ${application.lastName}`} icon={User} />
                <DataField label="Email Address" value={application.email} icon={Mail} />
                <DataField label="Phone Number" value={application.phone} icon={Phone} />
                <DataField label="Application Date" value={application.appliedDate ? new Date(application.appliedDate).toLocaleDateString() : "N/A"} icon={Calendar} />
              </div>
              
              {/* Address Information */}
              {(application.streetAddress || application.city) && (
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-900 mb-4 flex items-center">
                    <MapPin className="w-4 h-4 mr-2" />
                    Address
                  </h4>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {application.streetAddress && <p className="text-sm text-gray-900">{application.streetAddress}</p>}
                    {application.addressLine2 && <p className="text-sm text-gray-900">{application.addressLine2}</p>}
                    <p className="text-sm text-gray-900">
                      {[application.city, application.stateProvince, application.zipPostalCode].filter(Boolean).join(", ")}
                    </p>
                    {application.country && <p className="text-sm text-gray-900">{application.country}</p>}
                  </div>
                </div>
              )}
            </InfoCard>

            {/* Education */}
            {application.education && application.education.length > 0 && application.education.some(edu => edu.degree || edu.institution) && (
              <InfoCard title="Education" icon={GraduationCap}>
                <div className="space-y-4">
                  {application.education.map((edu, index) => (
                    (edu.degree || edu.institution || edu.yearCompleted) && (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <DataField label="Degree" value={edu.degree} />
                          <DataField label="Institution" value={edu.institution || edu.school} />
                          <DataField label="Field of Study" value={edu.fieldOfStudy} />
                          <DataField label="Year Completed" value={edu.yearCompleted || edu.graduationYear} />
                        </div>
                      </div>
                    )
                  ))}
                </div>
              </InfoCard>
            )}

            {/* Work Experience */}
            {application.workExperience && application.workExperience.length > 0 && application.workExperience.some(exp => exp.company) && (
              <InfoCard title="Work Experience" icon={Briefcase}>
                <div className="space-y-6">
                  {application.workExperience.map((exp, index) => (
                    exp.company && (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h4 className="text-lg font-semibold text-gray-900">{exp.position}</h4>
                            <p className="text-gray-600">{exp.company}</p>
                          </div>
                          {(exp.startDate || exp.endDate) && (
                            <div className="text-sm text-gray-500">
                              {exp.startDate && new Date(exp.startDate).toLocaleDateString()} - {exp.endDate ? new Date(exp.endDate).toLocaleDateString() : 'Present'}
                            </div>
                          )}
                        </div>
                        {exp.responsibilities && (
                          <div>
                            <p className="text-sm font-medium text-gray-700 mb-2">Responsibilities</p>
                            <p className="text-sm text-gray-600">{exp.responsibilities}</p>
                          </div>
                        )}
                      </div>
                    )
                  ))}
                </div>
              </InfoCard>
            )}

            {/* Previous Employment (for General Applications) */}
            {application.previousEmployment && application.previousEmployment.length > 0 && application.previousEmployment.some(emp => emp.company) && (
              <InfoCard title="Previous Employment" icon={Briefcase}>
                <div className="space-y-6">
                  {application.previousEmployment.map((emp, index) => (
                    emp.company && (
                      <div key={index} className="bg-gray-50 rounded-lg p-6">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">{emp.company}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                          <DataField label="Supervisor" value={emp.supervisor} />
                          <DataField label="Phone" value={emp.phone} />
                          <DataField label="Starting Salary" value={emp.startingSalary} />
                          <DataField label="Ending Salary" value={emp.endingSalary} />
                          <DataField label="Start Date" value={emp.startDate ? new Date(emp.startDate).toLocaleDateString() : null} />
                          <DataField label="End Date" value={emp.endDate ? new Date(emp.endDate).toLocaleDateString() : null} />
                          <DataField label="Reason for Leaving" value={emp.reasonForLeaving} />
                          <DataField label="May Contact" value={emp.mayContact ? "Yes" : "No"} />
                        </div>
                        {emp.responsibilities && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <p className="text-sm font-medium text-gray-700 mb-2">Responsibilities</p>
                            <p className="text-sm text-gray-600">{emp.responsibilities}</p>
                          </div>
                        )}
                      </div>
                    )
                  ))}
                </div>
              </InfoCard>
            )}

            {/* References */}
            {application.references && application.references.length > 0 && application.references.some(ref => ref.name) && (
              <InfoCard title="References" icon={Users}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {application.references.map((ref, index) => (
                    ref.name && (
                      <div key={index} className="bg-gray-50 rounded-lg p-4">
                        <h4 className="font-semibold text-gray-900 mb-3">{ref.name}</h4>
                        <div className="space-y-2">
                          <DataField label="Relationship" value={ref.relationship} />
                          <DataField label="Company" value={ref.company} />
                          <DataField label="Phone" value={ref.phone} />
                        </div>
                      </div>
                    )
                  ))}
                </div>
              </InfoCard>
            )}

            {/* Military Service */}
            {application.militaryService && Object.values(application.militaryService).some(value => value) && (
              <InfoCard title="Military Service" icon={Shield}>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <DataField label="Branch" value={application.militaryService.branch} />
                    <DataField label="Dates Served" value={application.militaryService.datesServed} />
                    <DataField label="Rank at Discharge" value={application.militaryService.rankAtDischarge} />
                    <DataField label="Type of Discharge" value={application.militaryService.typeOfDischarge} />
                  </div>
                  {application.militaryService.dischargeExplanation && (
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm font-medium text-gray-700 mb-2">Discharge Explanation</p>
                      <p className="text-sm text-gray-600">{application.militaryService.dischargeExplanation}</p>
                    </div>
                  )}
                </div>
              </InfoCard>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Resume & Documents */}
            <InfoCard title="Documents" icon={FileText} noPrint>
              {application.resumePath ? (
                <div className="space-y-3">
                  <button
                    onClick={handleViewResume}
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    View Resume
                  </button>
                  <p className="text-xs text-gray-500 text-center">Click to open resume in new tab</p>
                </div>
              ) : (
                <div className="text-center py-4">
                  <FileText className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No resume uploaded</p>
                </div>
              )}
            </InfoCard>

            {/* Quick Actions */}
            <InfoCard title="Quick Actions" icon={Edit} noPrint>
              <div className="space-y-3">
                <button
                  onClick={handlePrint}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                >
                  <Printer className="w-4 h-4 mr-2" />
                  Print Application
                </button>
                <Link
                  to="/admin/applications"
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Applications
                </Link>
              </div>
            </InfoCard>

            {/* Application Timeline */}
            <InfoCard title="Application Timeline" icon={Calendar} noPrint>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-2 h-2 bg-green-400 rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Application Submitted</p>
                    <p className="text-xs text-gray-500">
                      {new Date(application.appliedDate).toLocaleDateString()} at {new Date(application.appliedDate).toLocaleTimeString()}
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className={`flex-shrink-0 w-2 h-2 rounded-full mt-2 ${
                    application.status !== 'Pending' ? 'bg-blue-400' : 'bg-gray-300'
                  }`}></div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Status: {application.status || 'Pending'}</p>
                    <p className="text-xs text-gray-500">Current application status</p>
                  </div>
                </div>
              </div>
            </InfoCard>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ApplicationDetails;
